import React from 'react'
import { supabase } from '@/lib/supabase'
import { Label, Select, Input } from '@/shared/ui'

type AffiliateOption = {
  id: string
  name: string
  email: string
  code: string
}

type Props = {
  currentAffiliateId?: string | null
  parentAffiliateId: string | null
  onParentChange: (id: string | null) => void
  uplinePercent: number
  onUplinePercentChange: (value: number) => void
}

export default function AffiliateUplineFields({
  currentAffiliateId,
  parentAffiliateId,
  onParentChange,
  uplinePercent,
  onUplinePercentChange,
}: Props) {
  const [options, setOptions] = React.useState<AffiliateOption[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const { data: affRows, error: affErr } = await supabase
          .from('affiliates')
          .select('user_id, code, active, created_at')
          .order('created_at', { ascending: false })
        if (affErr) throw affErr
        const rows = (affRows || []) as any[]
        const ids = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[]
        let profiles: any[] = []
        if (ids.length) {
          const { data: profRows, error: profErr } = await supabase
            .from('v_profiles_contact_master')
            .select('id, full_name, email')
            .in('id', ids)
          if (profErr) throw profErr
          profiles = (profRows || []) as any[]
        }
        const byId = new Map(profiles.map((p) => [p.id, p]))
        const opts: AffiliateOption[] = rows
          .filter((r) => r.active !== false)
          .map((r) => {
            const p = byId.get(r.user_id) || {}
            return {
              id: String(r.user_id),
              name: String(p.full_name || ''),
              email: String(p.email || ''),
              code: String(r.code || ''),
            }
          })
          .filter((o) => !currentAffiliateId || o.id !== currentAffiliateId)
        if (!cancelled) setOptions(opts)
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Falha ao carregar afiliados para hierarquia')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [currentAffiliateId])

  function handleParentChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value || ''
    onParentChange(v ? v : null)
    if (!v) onUplinePercentChange(0)
  }

  function handleUplineChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = parseFloat(e.target.value)
    if (Number.isNaN(v)) {
      onUplinePercentChange(0)
    } else {
      onUplinePercentChange(Math.max(0, Math.min(100, v)))
    }
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Afiliado pai (upline)</Label>
        <Select value={parentAffiliateId || ''} onChange={handleParentChange} disabled={loading}>
          <option value="">Nenhum afiliado pai</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name || o.code || o.email || o.id}
            </option>
          ))}
        </Select>
        {error && <p className="text-xs text-destructive-foreground">{error}</p>}
        <p className="text-xs text-foreground/70">
          Selecione opcionalmente um afiliado pai para receber comissÕes upline em cima das vendas deste afiliado.
        </p>
      </div>

      <div className="space-y-2">
        <Label>% de comissÕo para o afiliado pai</Label>
        <Input
          type="number"
          min={0}
          max={100}
          step={0.1}
          value={Number.isFinite(uplinePercent) ? uplinePercent : 0}
          onChange={handleUplineChange}
          disabled={!parentAffiliateId}
        />
        <p className="text-xs text-foreground/70">
          Percentual da fatura do cliente indicado por este afiliado que serÁ pago ao afiliado pai como comissÕo upline.
          Ex.: 10 = 10%.
        </p>
      </div>
    </div>
  )
}
